import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useState, useEffect, lazy, Suspense } from "react";
import { SEO, generateFAQSchema } from "@/components/SEO";
import { ScreenshotGallery } from "@/components/landing/ScreenshotGallery";
import kaiPortraitOptimized from "@/assets/kai-portrait.png?format=webp&w=48";

// Use optimized kai-portrait
const kaiPortrait = kaiPortraitOptimized;

// Lazy load non-critical components to reduce initial bundle
const WelcomeVideoOverlay = lazy(() => import("@/components/WelcomeVideoOverlay").then(m => ({ default: m.WelcomeVideoOverlay })));
const FirstVisitPromoPopup = lazy(() => import("@/components/FirstVisitPromoPopup").then(m => ({ default: m.FirstVisitPromoPopup })));
const FloatingPromoBanner = lazy(() => import("@/components/FloatingPromoBanner").then(m => ({ default: m.FloatingPromoBanner })));

// TikTok Icon Component
const TikTokIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const Landing = () => {
  const navigate = useNavigate();
  const [promoPopupClosed, setPromoPopupClosed] = useState(false);
  const [showDeferredComponents, setShowDeferredComponents] = useState(false);

  // Defer non-critical components until after initial paint
  useEffect(() => {
    const timer = setTimeout(() => setShowDeferredComponents(true), 30000);
    return () => clearTimeout(timer);
  }, []);

  // FAQ data for structured data
  const faqData = [
    { question: "Is UnravelYou really free?", answer: "Yes! The core experience is 100% free. You can complete all 365 daily challenges, earn Spirit Shards, unlock daily rituals (tarot, horoscope, affirmations, mindfulness), and track your progress without spending anything. Premium readings and masterclasses are optional add-ons." },
    { question: "What are Spirit Shards?", answer: "Spirit Shards are our free in-app currency. You earn them by completing daily and weekly challenges. Use them to unlock daily rituals like tarot readings, personalized horoscopes, affirmations, and more. You can also save them up for premium features or convert them to Kai Credits." },
    { question: "How long does a reading take?", answer: "Your personalized reading is generated instantly. Full readings typically take 3-5 minutes to read thoroughly, though some users enjoy spending more time reflecting on each section." },
    { question: "Is my personal information secure?", answer: "Your privacy is paramount. We use bank-level encryption for all personal data. We never sell or share your information with third parties. All readings are private by default, and you control if and when to share them." },
    { question: "What makes UnravelYou different from other spiritual platforms?", answer: "UnravelYou uses a proprietary algorithm that combines ancient wisdom systems (numerology, astrology, BaZi) with modern computational analysis to create insights that are uniquely yours. Plus, our 365-day journey with daily challenges, Spirit Shards rewards, and gamified progress makes personal growth engaging and sustainable." },
    { question: "What information do you need from me?", answer: "We only need your full name and birth date to generate personalized insights. Optional: birth time and location for more precise readings. We never ask for sensitive personal or financial information." },
    { question: "Can I retake a reading?", answer: "Absolutely! Each reading can be retaken as many times as you like, and every single one will be unique. Our system learns from your progress, growth stage, and evolving journey—so every reading is personalized and adapted to your current context." },
    { question: "Is my information private?", answer: "Absolutely. Your readings are private by default. You control if and when you want to share them. We never sell your personal information or readings to third parties." },
    { question: "How does UnravelYou work?", answer: "UnravelYou uses a two-step process: First, Kai (our spiritual guide) personalizes and curates every reading using his proprietary algorithm based on your unique profile—birth date, numerology, astrology, and spiritual patterns. Then, AI renders these personalized insights beautifully in Kai's compassionate voice." },
    { question: "Does AI create my reading?", answer: "No. Kai creates and personalizes your reading using his proprietary algorithm. AI is only used to render the personalized insights in a beautiful, engaging format using Kai's voice and style." }
  ];

  return (
    <>
      <SEO
        title="UnravelYou - 365-Day Spiritual Journey & Daily Wellness Challenges"
        description="Free daily challenges, personalized tarot, horoscope, and spiritual insights. Start your 365-day self-discovery journey with UnravelYou - no credit card required."
        canonical="https://unravelyou.online/"
        structuredData={generateFAQSchema(faqData)}
      />
      <div className="min-h-screen" style={{ background: 'var(--gradient-hero)' }}>
      {/* Navigation */}
      <nav className="border-b border-nav/20 bg-nav shadow-md">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          {/* Left: Logo */}
          <Logo />
          
          {/* Center: Desktop/Tablet TikTok Follow - PROMINENT */}
          <a 
            href="https://www.tiktok.com/@kai.spiritualjourney" 
            target="_blank" 
            rel="noopener noreferrer"
            className="hidden md:inline-flex items-center gap-3 bg-black/30 hover:bg-black/50 transition-colors px-4 py-2 rounded-full border border-white/20"
          >
            <img src={kaiPortrait} alt="Kai" width={40} height={40} className="w-10 h-10 rounded-full object-cover border-2 border-white/40" decoding="async" loading="eager" />
            <span className="text-base text-white font-semibold">Follow Kai</span>
            <TikTokIcon className="w-5 h-5 text-white" />
          </a>
          
          {/* Right: Navigation Buttons */}
          <div className="flex items-center gap-3">
            {/* Desktop/Tablet Layout */}
            <div className="hidden md:flex items-start gap-3">
              <div className="flex flex-col items-center">
                <Button onClick={() => navigate('/register')} className="bg-gradient-primary h-6 px-1.5 text-sm mb-1">
                  <span className="font-semibold">Get FREE Account</span>
                </Button>
                <span className="text-[10px] leading-none text-green-600 dark:text-green-400 font-medium">
                  No credit card needed
                </span>
              </div>
              <Button onClick={() => navigate('/signin')} className="bg-gradient-secondary h-[52px] px-5 rounded-lg shadow-md hover:shadow-lg transition-all border border-white/10 flex items-center justify-center">

<div className="flex flex-col items-center justify-center leading-none">
  <div className="flex items-center gap-1.5 mb-0.5">
    <svg className="w-3 h-3" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.26.81-.58z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
    <span className="font-bold text-[11px] md:text-xs">Sign in</span>
  </div>
  <span className="font-bold text-[10px] md:text-[11px]">with Google</span>
</div>

              </Button>
            </div>

            {/* Mobile Layout - Reorganized */}
            <div className="flex md:hidden items-stretch gap-2 max-h-16">
              {/* Column 1: Get FREE Account + Follow Kai (bigger) */}
              <div className="flex flex-col items-center gap-1">
                <Button onClick={() => navigate('/register')} className="bg-gradient-primary h-7 px-2 text-xs">
                  <span className="font-semibold">Get FREE Account</span>
                </Button>
                <a 
                  href="https://www.tiktok.com/@kai.spiritualjourney" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/20 hover:bg-black/40 transition-colors"
                >
                  <img src={kaiPortrait} alt="Kai" width={24} height={24} className="w-6 h-6 rounded-full object-cover border border-white/30" decoding="async" loading="eager" />
                  <span className="text-xs text-white/90 font-medium">Follow Kai</span>
                  <TikTokIcon className="w-4 h-4 text-white" />
                </a>
              </div>

              {/* Column 2: Sign In + No credit card needed */}
              <div className="flex flex-col items-center gap-0.5 h-full">
                <Button onClick={() => navigate('/signin')} className="bg-gradient-secondary h-auto py-2 px-3 rounded-lg shadow-sm flex items-center justify-center">

<div className="flex flex-col items-center justify-center leading-none">
  <div className="flex items-center gap-1.5 mb-0.5">
    <svg className="w-3 h-3" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.26.81-.58z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
    <span className="font-bold text-[11px] md:text-xs">Sign in</span>
  </div>
  <span className="font-bold text-[10px] md:text-[11px]">with Google</span>
</div>

                </Button>
                <div className="flex-1 flex flex-col items-center justify-center leading-tight">
                  <span className="text-[8px] text-green-600 dark:text-green-400 font-medium">No credit card</span>
                  <span className="text-[8px] text-green-600 dark:text-green-400 font-medium">needed</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Compact with Unified CTA */}
      <section className="container mx-auto px-4 pt-8 pb-0 text-center text-white">
        {/* Main branding badge */}
        <div className="mb-1 text-center">
          <div className="relative inline-flex items-center gap-1 md:gap-2 justify-center mb-3 px-4 md:px-8 py-2 md:py-3 bg-yellow-400 rounded-lg shadow-lg border-2 border-yellow-600">
            <span className="text-2xl md:text-6xl lg:text-7xl font-bold text-black drop-shadow-[0_2px_4px_rgba(0,0,0,0.2)]">UnravelYou</span>
            <span className="text-lg md:text-4xl lg:text-5xl text-black/50">-</span>
            <span className="text-lg md:text-4xl lg:text-5xl font-medium text-black/80 whitespace-nowrap">Discover your Path</span>
<a href="/about" className="absolute bottom-0 right-2 md:bottom-1.5 md:right-4 text-[10px] md:text-sm text-white underline opacity-90 hover:opacity-100 transition-opacity">About UnravelYou</a>
          </div>
        </div>

        {/* Unified CTA Block */}
        <div className="flex justify-center w-full">
          <Button onClick={() => navigate('/register')} className="bg-gradient-primary h-auto pt-2 pb-1 px-12 font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all flex flex-col items-center gap-1 rounded-xl min-w-[320px] md:min-w-[400px]">
            <span className="text-xl md:text-2xl font-bold">Create Your Free Account</span>
            <span className="text-xs text-green-400 font-semibold flex flex-wrap justify-center gap-2 leading-none pb-1">
              <span>FREE to Start</span>
              <span>•</span>
              <span>No Credit Card Needed</span>
            </span>
          </Button>
        </div>
      </section>

      {/* Screenshot Gallery - LCP element, loaded eagerly */}
      <ScreenshotGallery />

      {/* Footer with Professional Disclaimer */}
      <footer className="bg-black/50 py-12 mt-12 border-t border-white/10">
        <div className="container mx-auto px-4 text-center">
          <p className="text-white/40 text-xs max-w-3xl mx-auto leading-relaxed mb-8">
            <strong>Professional Disclaimer:</strong> UnravelYou combines ancient wisdom traditions with modern technology for self-discovery, educational, and entertainment purposes. While our calculations are mathematically precise based on the birth data provided, they should not be considered a substitute for professional medical, legal, or financial advice. Your destiny remains in your hands; our insights are maps to help you navigate, but you are always the driver of your own life choices.
          </p>
          <nav className="flex justify-center gap-6 text-sm text-white/60" aria-label="Footer navigation">
            <a href="mailto:kai@kairosmedialab.com" className="hover:text-white transition-colors">Contact Support</a>
          </nav>
          <p className="text-white/30 text-xs mt-8">
            © {new Date().getFullYear()} UnravelYou. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Welcome Video & Promo Popup & Banner for visitor conversion (lazy loaded) */}
      {showDeferredComponents && (
        <Suspense fallback={null}>
          <WelcomeVideoOverlay />
          <FirstVisitPromoPopup onClose={() => setPromoPopupClosed(true)} />
          <FloatingPromoBanner hideTemporarily={promoPopupClosed} />
        </Suspense>
      )}
      </div>
    </>
  );
};

export default Landing;
